import React from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import {
  InputLabel,
  TextField,
  FormHelperText,
  Autocomplete,
} from "@mui/material";

import messages from "../../assets/locale/messages";
import "./Select.scss";

const getOptionLabelSafe = (option) => {
  if (!option) return "";
  if (typeof option === "string" || typeof option === "number")
    return String(option);
  return option.label ?? option.name ?? "";
};

const isOptionEqualToValueSafe = (option, value) => {
  if (!option || !value) return false;
  // Prefer id comparison when present, fallback to label/name string comparison.
  if (option.id != null && value.id != null) return option.id === value.id;
  return (
    getOptionLabelSafe(option) !== "" &&
    getOptionLabelSafe(option) === getOptionLabelSafe(value)
  );
};

const Select = ({
  labelClassName,
  value,
  inputClassName,
  disabled,
  label,
  options,
  placeholder,
  id,
  name,
  hasError,
  required,
  onChange,
  errMsg,
  helperText,
  helperTextClass,
  isMultipleSelect,
  wrapperClass,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { inputsValidations, shared } = messages[lang];

  return (
    <div
      className={`d-flex justify-content-center flex-column ${wrapperClass} ${
        isMultipleSelect ? "multiple-select-wrapper" : "single-select-wrapper"
      }`}>
      <InputLabel htmlFor={id || name} className={labelClassName}>
        <>
          {label}
          {required ? <span className="err"> *</span> : ""}
        </>
      </InputLabel>
      <Autocomplete
        className={`${inputClassName} custom-select-style ${hasError ? "error-border" : ""} ${
          isMultipleSelect && value?.length && "removed-placeholder"
        }`}
        name={name}
        multiple={isMultipleSelect}
        id={id || name}
        options={options}
        disabled={disabled}
        getOptionLabel={getOptionLabelSafe}
        isOptionEqualToValue={isOptionEqualToValueSafe}
        noOptionsText={shared.noOptions}
        value={value}
        onChange={(e, value) => {
          onChange(value);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            className={`${
              isMultipleSelect === true ? "multi-select-style" : ""
            } ${lang === "en" ? "" : "rtl-icons-direction-handel"}`}
          />
        )}
      />
      <FormHelperText
        error={hasError}
        className={`${helperTextClass || "font-medium fsize-12"} ${
          lang === "en" ? "" : "text-end"
        }`}>
        {hasError ? inputsValidations[errMsg] : helperText}
      </FormHelperText>
    </div>
  );
};

export default Select;

Select.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  labelClassName: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  inputClassName: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string,
    })
  ),
  placeholder: PropTypes.string,
  name: PropTypes.string.isRequired,
  id: PropTypes.string,
  required: PropTypes.bool,
  hasError: PropTypes.bool,
  helperText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  helperTextClass: PropTypes.string,
  errMsg: PropTypes.string,
  value: PropTypes.any,
  isMultipleSelect: PropTypes.bool,
  wrapperClass: PropTypes.string,
};
